import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { BOOKING_CALENDAR_PRODUCT } from '../../../../brands/default/content/components/deal';
import { getSelectValues } from '../../../../helpers/checkout';
import { formatCurrency } from '../../../../helpers/currency';
import ThemeContext from '../../../../providers/ThemeProvider';
import DisabledComponent from '../../../_generic/DisabledComponent';
import CustomNativeSelect from '../../../_generic/custom-native-select/CustomNativeSelect';
import VipExclusiveTag from '../VipExclusiveTag';

const BookingCalendarProduct = ({
  currency,
  isBCOR,
  isCYF,
  priceDiff,
  product,
  quantity,
  selected,
  onSelect,
  onQuantityChange,
}) => {
  const theme = useContext(ThemeContext);
  const isVipUser = useSelector((state) => state.user.userprofile.vipUser);

  const { checkIn, checkOut, now, save, was, date } = BOOKING_CALENDAR_PRODUCT;

  const isMarketingCreditProduct = isVipUser && product?.vipDiscountAllowed;
  const finalPrice = isMarketingCreditProduct
    ? product.vipPrice
    : product.price;

  const finalSavePercent = isMarketingCreditProduct
    ? product.vipDiscountPercentage
    : product.discount;

  /** 1 - max purchasable capped at 30 */
  const options = getSelectValues(
    1,
    Math.min(product.purchasableQuantity, product.quantityAvailable, 30),
  );

  const onClick = () => {
    if (product.productAvailable) {
      onSelect(product);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && product.productAvailable) {
      onSelect(product);
    }
  };

  return (
    <>
      <div
        aria-label={`Select ${product.friendlyName}`}
        className={classNames('booking-calendar__product', {
          'booking-calendar__product--disabled': !product.productAvailable,
          'booking-calendar__product--selected': selected,
        })}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
      >
        <h3 className="booking-calendar__product-title">
          {product.friendlyName}{' '}
          {product?.vipExclusive && (
            <VipExclusiveTag className={'travel-exculsive-tag'} />
          )}
        </h3>
        <div className="booking-calendar__product-desc">
          {!isBCOR && (
            <div className="booking-calendar__product-dates">
              {/* SAME DATE */}
              {product.checkInDate === product.checkOutDate && (
                <p className="booking-calendar__product-date">
                  <b>{date}: </b>
                  <span>{product.checkInDate}</span>
                </p>
              )}
              {/* DIFFERENT DATES */}
              {product.checkInDate !== product.checkOutDate && (
                <>
                  <p className="booking-calendar__product-date">
                    <b>{checkIn}: </b>
                    <span>{product.checkInDate}</span>
                  </p>
                  <p className="booking-calendar__product-date">
                    <b>{checkOut}: </b>
                    <span>{product.checkOutDate}</span>
                  </p>
                </>
              )}
            </div>
          )}
          <div className="booking-calendar__product-prices">
            {product.discountRepresentation === 'PERCENTAGE' && (
              <>
                <div className="booking-calendar__product-was">
                  <p className="booking-calendar__product-label">{was}</p>
                  <p className="booking-calendar__product-was-strike booking-calendar__product-value">
                    <span>
                      {formatCurrency(
                        currency,
                        Number.parseFloat(product.originalPrice),
                        false,
                        true,
                      )}
                    </span>
                  </p>
                </div>
                <div className="booking-calendar__product-savings">
                  <p className="booking-calendar__product-label">{save}</p>
                  <p className="booking-calendar__product-value">
                    {finalSavePercent}%
                  </p>
                </div>
              </>
            )}
            <div className="booking-calendar__product-price">
              <p className="booking-calendar__product-label">{now}</p>
              <p className="booking-calendar__product-value">
                {formatCurrency(
                  currency,
                  Number.parseFloat(finalPrice) + priceDiff,
                  false,
                  true,
                )}
              </p>
            </div>
          </div>
        </div>
        {!isCYF && selected && product.showQuantity && (
          <label className="booking-calendar__quantity">
            Quantity:
            <CustomNativeSelect
              onChange={(event) =>
                onQuantityChange(Number.parseInt(event.target.value, 10))
              }
              value={quantity}
            >
              {options.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </CustomNativeSelect>
          </label>
        )}
        <DisabledComponent isDisabled={!product.productAvailable} />
      </div>
      <style jsx>{`
        .booking-calendar__product {
          position: relative;
          padding: 10px;
          border-radius: 4px;
          border: 2px solid transparent;
          margin-bottom: 8px;
          background-color: #f0f0f0;
          outline: none;
        }
        .booking-calendar__product:last-child {
          margin-bottom: 0;
        }

        .booking-calendar__product:focus,
        .booking-calendar__product:hover {
          border: 2px solid #cccccc;
        }
        .booking-calendar__product--selected {
          border: 2px solid ${theme.colors.primary} !important;
        }
        .booking-calendar__product--selected .booking-calendar__product-title {
          color: ${theme.colors.primary};
        }
        .booking-calendar__product-was-strike span {
          position: relative;
        }
        .booking-calendar__product-was-strike span:before {
          border-top: 1px solid #000;
          content: '';
          left: 0;
          margin-left: -15%;
          position: absolute;
          right: 0;
          top: 50%;
          transform: rotate(-11deg);
          width: 130%;
        }
        .booking-calendar__product-desc {
          display: flex;
          align-items: center;
        }
        .booking-calendar__product-dates {
          margin-right: 20px;
        }
        .booking-calendar__product-date {
          margin: 0;
        }
        .booking-calendar__product-prices {
          display: flex;
          align-items: center;
          justify-content: space-around;
          flex-grow: 1;
          padding: 8px;
          background-color: white;
          border: 1px solid #cccccc;
          border-radius: 4px;
          text-align: center;
        }
        .booking-calendar__product-was,
        .booking-calendar__product-savings,
        .booking-calendar__product-price {
          width: calc(100% / 3);
        }
        .booking-calendar__product-label {
          text-transform: capitalize;
          font-weight: bold;
          margin: 0;
          line-height: 1em;
          font-size: 12px;
        }
        .booking-calendar__product-value {
          font-size: 18px;
          margin: 0;
        }
        .booking-calendar__product-price .booking-calendar__product-value {
          color: ${theme.colors.primary};
        }
        .booking-calendar__quantity {
          font-weight: bold;
        }
        :global(.booking-calendar__quantity .custom-select) {
          margin-left: 8px;
        }
        .booking-calendar__product--disabled {
          pointer-events: none;
        }
      `}</style>
    </>
  );
};

BookingCalendarProduct.propTypes = {
  currency: PropTypes.string,
  isBCOR: PropTypes.bool,
  isCYF: PropTypes.bool,
  onQuantityChange: PropTypes.func,
  onSelect: PropTypes.func,
  priceDiff: PropTypes.number,
  product: PropTypes.object,
  quantity: PropTypes.number,
  selected: PropTypes.bool,
};

BookingCalendarProduct.defaultProps = {
  currency: 'gbp',
  isBCOR: false,
  isCYF: false,
  onQuantityChange: () => false,
  onSelect: () => false,
  priceDiff: 0,
  product: {},
  quantity: 1,
  selected: false,
};

export default BookingCalendarProduct;
